import { ColorV2 } from "config/colors";
import { toRgba } from "config/colors.rgba.logic";
import { AbsenceType, ScheduleStatus } from "model/Schedule";
import type { StaffRegisterStatus } from "services/api/company/timeReports.service";
import { getBlockStatusStyling } from "web-apps/company/components/schedule/utils/schedule.status";
import {
  getAbsenceTypeLabel,
  isAbsenceType,
} from "web-apps/company/utils/absenceLabels";
import {
  type StaffRegisterStatusTone,
  toStaffRegisterStatusIconColor,
  toStaffRegisterStatusIconName,
  toStaffRegisterStatusText,
  toStaffRegisterStatusTone,
} from "web-apps/company/components/timeReportsV2/components/views/staffRegister/staffRegisterView.status.mapper";
import type { DayShift } from "./scheduleDayShiftsPanel.logic";

export interface DayShiftStatusPresentation {
  readonly backgroundColor: string;
  readonly iconColor: ColorV2;
  readonly iconName: string;
  readonly text: string;
  readonly textColor: ColorV2;
}

interface DayShiftStatusParams {
  readonly clockStatus: StaffRegisterStatus | null;
  readonly isToday: boolean;
  readonly shift: DayShift;
  readonly translate: Translate;
}

type Translate = (key: string) => string;

interface ClockStatusParams {
  readonly status: StaffRegisterStatus;
  readonly translate: Translate;
}

interface AbsencePresentationParams {
  readonly status:
    | ScheduleStatus.PLANNED_ABSENCE
    | ScheduleStatus.UNPLANNED_ABSENCE;
  readonly text: string;
}

interface FindDayShiftClockStatusParams {
  readonly shift: DayShift;
  readonly statuses: ReadonlyMap<number, StaffRegisterStatus>;
}

interface ScheduleStatusParams {
  readonly shift: DayShift;
  readonly translate: Translate;
}

const toDangerPresentation = (text: string): DayShiftStatusPresentation => ({
  backgroundColor: ColorV2.NegativeRedLight,
  iconColor: ColorV2.NegativeRed,
  iconName: "v2/warning-triangle",
  text,
  textColor: ColorV2.NegativeRed,
});

const toInfoPresentation = (text: string): DayShiftStatusPresentation => ({
  backgroundColor: toRgba(ColorV2.ElectricBlue, 0.14),
  iconColor: ColorV2.ElectricBlue,
  iconName: "v2/calendar",
  text,
  textColor: ColorV2.ElectricBlue,
});

const toAbsencePresentation = ({
  status,
  text,
}: AbsencePresentationParams): DayShiftStatusPresentation => {
  const styling = getBlockStatusStyling(status);
  return {
    backgroundColor: styling.backgroundColor,
    iconColor: styling.color,
    iconName: "v2/absent",
    text,
    textColor: styling.color,
  };
};

const toClockStatusBackgroundColor = (
  tone: StaffRegisterStatusTone,
): string => {
  if (tone === "success") return ColorV2.PositiveGreenLight;
  if (tone === "warning") return ColorV2.UnplannedAbsenceYellow;
  if (tone === "info") return toRgba(ColorV2.ElectricBlue, 0.14);
  if (tone === "danger") return ColorV2.NegativeRedLight;
  return ColorV2.Blue6;
};

const findAbsenceResolution = (shift: DayShift): AbsenceType | null => {
  for (const detail of shift.details) {
    if (isAbsenceType(detail.resolution)) return detail.resolution;
  }
  return null;
};

export const findDayShiftClockStatus = ({
  shift,
  statuses,
}: FindDayShiftClockStatusParams): StaffRegisterStatus | null => {
  for (const detail of shift.details) {
    const status = statuses.get(detail.timeReportId);
    if (status) return status;
  }
  return null;
};

const toClockStatusText = ({
  status,
  translate,
}: ClockStatusParams): string => {
  if (status === "Started") return translate("TimeReports.StampedIn");
  if (status === "Stamped") return translate("TimeReports.StampedOut");
  return toStaffRegisterStatusText(status, translate);
};

const toClockPresentation = ({
  status,
  translate,
}: ClockStatusParams): DayShiftStatusPresentation => {
  const tone = toStaffRegisterStatusTone(status);
  return {
    backgroundColor: toClockStatusBackgroundColor(tone),
    iconColor: toStaffRegisterStatusIconColor(status),
    iconName: toStaffRegisterStatusIconName(status) ?? "v2/info",
    text: toClockStatusText({ status, translate }),
    textColor: toStaffRegisterStatusIconColor(status),
  };
};

const toScheduleStatusPresentation = ({
  shift,
  translate,
}: ScheduleStatusParams): DayShiftStatusPresentation | null => {
  if (
    shift.status === ScheduleStatus.PLANNED_ABSENCE ||
    shift.status === ScheduleStatus.UNPLANNED_ABSENCE
  ) {
    const absence = findAbsenceResolution(shift);
    const text = absence
      ? getAbsenceTypeLabel(absence, translate)
      : translate(
          shift.status === ScheduleStatus.PLANNED_ABSENCE
            ? "Schedule.PlannedAbsence"
            : "Schedule.UnplannedAbsence",
        );
    return toAbsencePresentation({ status: shift.status, text });
  }
  if (shift.status === ScheduleStatus.DECLINED)
    return toDangerPresentation(translate("Schedule.Declined"));
  if (shift.status === ScheduleStatus.NOT_AVAILABLE)
    return toInfoPresentation(translate("Schedule.NotAvailable"));
  return null;
};

export const toDayShiftStatusPresentation = ({
  clockStatus,
  isToday,
  shift,
  translate,
}: DayShiftStatusParams): DayShiftStatusPresentation | null => {
  const scheduleStatus = toScheduleStatusPresentation({ shift, translate });
  if (scheduleStatus) return scheduleStatus;
  if (!isToday || shift.isUnassigned) return null;
  return clockStatus
    ? toClockPresentation({ status: clockStatus, translate })
    : null;
};
